<template>
	<div ref="el" data-component="rive-asset" :class="`${asset.file ? 'canvas' : 'image'} ${layout}`" :style="`--ratio: ${width} / ${height}`">
		<canvas v-if="asset.file" :data-file="asset.file" :width="`${width ? width : ''}`" :height="`${height ? height : ''}`" />
		<ImageAsset v-else-if="asset.fallbackImage" :asset="asset.fallbackImage" />
	</div>
</template>

<script setup>
import { Rive, Layout, Fit, Alignment } from '@rive-app/webgl2'

import { ImageAsset, getActiveContext } from '~/components/core'

import { useInViewport } from '~/composables/core'

const emit = defineEmits(['state-change', 'rive-event'])

const props = defineProps({
	asset: { type: [Boolean, Object], default: false },
	autoplay: { type: Boolean, default: false },
	layout: { type: String, default: 'contain' },
	stateMachine: { type: [Boolean, String], default: 'State Machine 1' },
	artboard: { type: [Boolean, String], default: false },
	width: { type: [Boolean, Number], default: false },
	height: { type: [Boolean, Number], default: false },
})

const el = ref(null)
const { viewport } = useInViewport(el)

let animation
let animationName = ''
let inputs
let viewModelInstance
let isPlaying = false

let loadPromise
let resolveLoad

// Create a new promise
const createLoadPromise = () => {
	loadPromise = new Promise((resolve) => {
		resolveLoad = resolve
	})
}

const init = () => {
	const canvas = el.value.querySelector('canvas')
	if (!canvas) return

	const file = canvas.dataset.file

	const fitType = props.layout === 'cover' ? Fit.Cover : Fit.Contain
	const fitAlignment = props.layout === 'cover' ? Alignment.Center : Alignment.Center
	const layout = props.layout ? new Layout({ fit: fitType, alignment: fitAlignment }) : null

	animation = new Rive({
		src: file,
		canvas,
		layout,
		stateMachines: props.stateMachine,
		artboard: props.artboard,
		autoBind: true,
		onLoad: () => {
			if (!props.width || !props.height) animation.resizeDrawingSurfaceToCanvas()

			animationName = animation.animationNames[0]
			inputs = animation?.stateMachineInputs(props.stateMachine)
			viewModelInstance = animation?.viewModelInstance

			// Resolve the promise when loaded
			resolveLoad()

			watch(
				viewport,
				(value) => {
					if (props.autoplay && value.inViewport) {
						play()
					}
					if (!value.inViewport && isPlaying) {
						pause()
					}
				},
				{ immediate: true }
			)
		},
		onStateChange: (event) => {
			emit('state-change', { event, inputs: getInputs() })
		},
		onRiveEvent: (event) => {
			emit('rive-event', event)
		},
	})
}

const play = () => {
	// animation?.reset()
	animation?.play(props.stateMachine)
	isPlaying = true
}
const pause = () => {
	animation?.pause()
	isPlaying = false
}
const reset = () => {
	animation?.reset()
	isPlaying = false
}

const scrub = (value) => animation?.scrub(animationName, value)
const getInputs = () => {
	if (animation && props.stateMachine) {
		const currentInputs = animation.stateMachineInputs(props.stateMachine)
		if (currentInputs && currentInputs.length > 0) {
			inputs = currentInputs // Update cache
			return currentInputs
		}
	}
	return inputs || []
}
const getInput = (name) => {
	const allInputs = getInputs()
	return allInputs.find((input) => input.name === name)
}
const setRiveInput = (name, value) => {
	const allInputs = animation?.stateMachineInputs(props.stateMachine)
	const input = allInputs?.find((item) => item.name === name)
	if (!input) return false

	if (value === 'fire') {
		if (typeof input.fire !== 'function') return false

		input.fire()
		return true
	}

	input.value = value

	return true
}
const getViewModelInstance = () => viewModelInstance

const kill = () => {
	if (animation) {
		// animation.cleanup()
		animation = null
	}
}

watch(
	() => screen.width,
	() => {
		kill()
		nextTick(() => init())
	}
)

onBeforeUnmount(() => kill())

onMounted(() => {
	createLoadPromise()

	// init()

	const context = getActiveContext()
	context.loader.loaded.then(() => init())
})

defineExpose({
	play,
	pause,
	reset,
	getAnimation: () => animation,
	scrub: scrub,
	getInput,
	getInputs,
	setRiveInput,
	getViewModelInstance,
	kill: kill,
	loaded: () => loadPromise,
})
</script>

<style lang="scss" scoped>
[data-component='rive-asset'] {
	position: relative;

	&.contain {
		aspect-ratio: var(--ratio);
	}

	&.canvas {
		width: 100%;
		height: 100%;
	}

	canvas {
		width: 100%;
		height: 100%;

		object-fit: cover;
	}

	img {
		width: 100%;
	}
}
</style>
